import { r, s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { Box, Checkbox, Popover, Text } from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives';
import type { PopoverProps } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

type CheckboxPopoverOption = {
  key: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (nextChecked: boolean) => void;
};

type CheckboxPopoverSection = {
  key: string;
  title?: string;
  options: CheckboxPopoverOption[];
  emptyText?: string;
};

type CheckboxPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  heading?: string;
  headingIcon?: ReactNode;
  sections: CheckboxPopoverSection[];
  minWidth?: number;
  paperSx?: SxProps<Theme>;
  anchorOrigin?: PopoverProps['anchorOrigin'];
  transformOrigin?: PopoverProps['transformOrigin'];
};

const basePaperSx = {
  backgroundColor: 'background.paper',
  backgroundImage: 'none',
  '--Paper-overlay': 'none',
  '--Paper-shadow': 'none',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: `${r('md')} !important`,
  overflow: 'hidden !important',
  boxShadow: 'none',
  minWidth: 0,
  width: 'auto',
  maxWidth: '90vw',
} as const;

export const CheckboxPopover = ({
  open,
  anchorEl,
  onClose,
  heading,
  headingIcon,
  sections,
  minWidth,
  paperSx,
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
}: CheckboxPopoverProps): ReactElement => {
  const optionRowMinHeight = '32px';
  const resolvedPaperSx = [
    basePaperSx,
    minWidth !== undefined ? { minWidth } : null,
    paperSx ?? null,
  ].filter(Boolean) as SxProps<Theme>;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      sx={{
        '& .MuiPopover-paper': {
          borderRadius: `${r('md')} !important`,
          overflow: 'hidden !important',
        },
      }}
      paperSx={resolvedPaperSx}
      slotProps={{
        paper: {
          square: false,
          elevation: 0,
        },
      }}
    >
      <Stack gap="none">
        {heading ? (
          <Stack
            gap="none"
            sx={{
              px: s('lg'),
              py: s('xs'),
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap="sm"
              sx={{ '& .MuiSvgIcon-root': { color: 'text.secondary' } }}
            >
              {headingIcon ?? null}
              <Text role="label" color="textSecondary">
                {heading}
              </Text>
            </Stack>
          </Stack>
        ) : null}
        <Stack gap="none" sx={{ p: s('none') }}>
          {sections.map((section, sectionIndex) => (
            <Stack key={section.key} gap="none">
              {sectionIndex > 0 ? (
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', my: s('xxs') }} />
              ) : null}
              {section.title ? (
                <Text role="label" color="textSecondary" sx={{ px: s('md'), py: s('xxs') }}>
                  {section.title}
                </Text>
              ) : null}
              {section.options.length > 0 ? (
                section.options.map((option) => (
                  <Stack
                    key={option.key}
                    direction="row"
                    alignItems="center"
                    gap="sm"
                    role="button"
                    aria-disabled={option.disabled ? 'true' : undefined}
                    tabIndex={option.disabled ? -1 : 0}
                    onClick={() => {
                      if (option.disabled) return;
                      option.onChange(!option.checked);
                    }}
                    onKeyDown={(event) => {
                      if (option.disabled) return;
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      option.onChange(!option.checked);
                    }}
                    sx={{
                      px: s('md'),
                      py: s('none'),
                      pr: s('md'),
                      width: '100%',
                      minWidth: 0,
                      minHeight: optionRowMinHeight,
                      cursor: option.disabled ? 'not-allowed' : 'pointer',
                      backgroundColor: 'transparent',
                      '&:hover': option.disabled ? undefined : { backgroundColor: 'action.hover' },
                      '&:focus-visible': option.disabled
                        ? undefined
                        : { backgroundColor: 'action.hover' },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={option.checked}
                      disabled={option.disabled}
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      onChange={(event) => {
                        option.onChange(event.target.checked);
                      }}
                      sx={{
                        p: s('none'),
                        m: s('none'),
                        mr: s('xs'),
                        '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                      }}
                    />
                    <Text role="body" sx={{ whiteSpace: 'nowrap' }}>
                      {option.label}
                    </Text>
                  </Stack>
                ))
              ) : section.emptyText ? (
                <Text role="body" color="textSecondary" sx={{ px: s('md'), py: s('xs') }}>
                  {section.emptyText}
                </Text>
              ) : null}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Popover>
  );
};

export type { CheckboxPopoverOption, CheckboxPopoverProps, CheckboxPopoverSection };
